import { useState, useCallback, useRef, useEffect } from 'react'; // <-- 1. IMPORT useEffect
import type { AudioEngine, SynthParams, DrumSound, KickParams, SnareParams, HatParams, PartSequence } from '../types';
import { noteToFrequency, NUM_STEPS } from '../constants';

export const useAudioEngine = (pyodide: any) => {
  const [isReady, setIsReady] = useState(false);
  const audioEngineRef = useRef<AudioEngine | null>(null);
  const noiseBufferRef = useRef<AudioBuffer | null>(null);
  const ambianceSourceNodeRef = useRef<AudioBufferSourceNode | null>(null);
  const ambianceGainNodeRef = useRef<GainNode | null>(null);
  const loadedAmbianceBuffersRef = useRef<Map<string, AudioBuffer>>(new Map());
  const rendererWorkerRef = useRef<Worker | null>(null);

  // 2. CREATE A REF to hold the pyodide prop
  const pyodideRef = useRef(pyodide);

  // 3. UPDATE THE REF whenever the prop changes
  useEffect(() => {
    pyodideRef.current = pyodide;
  }, [pyodide]);

  const initializeAudio = useCallback(async () => {
    if (audioEngineRef.current) return;

    const context = new (window.AudioContext || (window as any).webkitAudioContext)();
    
    if (context.state === 'suspended') {
      await context.resume();
    }

    // Create a white noise buffer
    const bufferSize = context.sampleRate * 2;
    const buffer = context.createBuffer(1, bufferSize, context.sampleRate);
    const output = buffer.getChannelData(0);
    for (let i = 0; i < bufferSize; i++) {
      output[i] = Math.random() * 2 - 1;
    }
    noiseBufferRef.current = buffer;

const playSynth = (params: SynthParams, note: string, time: number, destination: AudioNode = context.destination) => {
  const isPyodideWave = params.waveform.startsWith('pyodide-');
  const noteDuration = params.attack + params.decay;

  // --- Gain Envelope (used by both) ---
  const gain = context.createGain();
  gain.gain.setValueAtTime(0, time);
  gain.gain.linearRampToValueAtTime(params.volume, time + params.attack);
  gain.gain.linearRampToValueAtTime(0, time + noteDuration);

  // --- Delay (used by both) ---
  let outputNode: AudioNode = destination;
  if (params.delayMix > 0 && params.delayTime > 0) {
    // ... (delay logic is unchanged)
    const dryGain = context.createGain();
    const wetGain = context.createGain();
    const delay = context.createDelay(1.0);
    const feedback = context.createGain();

    dryGain.gain.setValueAtTime(1.0 - params.delayMix, time);
    wetGain.gain.setValueAtTime(params.delayMix, time);
    delay.delayTime.setValueAtTime(params.delayTime, time);
    feedback.gain.setValueAtTime(params.delayFeedback, time);

    gain.connect(dryGain);
    dryGain.connect(destination);

    gain.connect(delay);
    delay.connect(feedback);
    feedback.connect(delay);
    delay.connect(wetGain);
    wetGain.connect(destination);
    
    outputNode = gain; 
  } else {
    gain.connect(destination);
    outputNode = gain;
  }

  // --- Waveform Generation ---
  // 5. USE THE REF (pyodideRef.current)
  if (isPyodideWave && pyodideRef.current) {
    // --- NEW: Pyodide Audio Generation ---
    try {
      // 1. Set sample rate in Python
      pyodideRef.current.globals.get('set_sample_rate')(context.sampleRate); // <-- Use ref

      // 2. Get parameters
      const baseFreq = noteToFrequency(note);
      const freqWithPitch = baseFreq * Math.pow(2, params.pitch / 12);
      const pyOscType = params.waveform.split('-')[1]; // 'saw', 'square', 'sine'
      
      // 3. Call Python!
      let pyProxy = pyodideRef.current.globals.get('generate_wave')( // <-- Use ref
          freqWithPitch,
          noteDuration, // Use AD envelope duration
          pyOscType,
          params.filterCutoff,
          params.filterResonance
      );

      // 4. Copy data from Python (64-bit) to JS (32-bit)
      const audioSamples = pyProxy.toJs({ array_buffer_type: "float32" });
      pyProxy.destroy(); // Free memory

      // 5. Create Web Audio Buffer
      const buffer = context.createBuffer(
          1, 
          audioSamples.length, 
          context.sampleRate
      );
      buffer.getChannelData(0).set(audioSamples);

      // 6. Create a player (BufferSource) and schedule it
      const source = context.createBufferSource();
      source.buffer = buffer;
      
      // 7. Connect to the envelope and schedule playback
      source.connect(outputNode); // Connect to our 'gain' node
      source.start(time);
      source.stop(time + noteDuration + 0.05); // Stop buffer playback
      
    } catch (e) {
        console.error("Pyodide synth error:", e);
    }

  // 5. USE THE REF (pyodideRef.current)
  } else if (isPyodideWave && !pyodideRef.current) {
      console.warn("Pyodide not ready, skipping synth trigger.");
  } else {
    // --- ORIGINAL: Web Audio API Generation ---
    // ... (this part is unchanged)
    const baseFreq = noteToFrequency(note);
    const freqWithPitch = baseFreq * Math.pow(2, params.pitch / 12);

    const osc = context.createOscillator();
    // @ts-ignore
    osc.type = params.waveform as OscillatorType;
    osc.frequency.setValueAtTime(freqWithPitch, time);

    const filter = context.createBiquadFilter();
    filter.type = 'lowpass';
    filter.frequency.setValueAtTime(params.filterCutoff, time);
    filter.Q.setValueAtTime(params.filterResonance, time);

    osc.connect(filter);
    filter.connect(outputNode);

    osc.start(time);
    osc.stop(time + noteDuration + 0.05);
  }
};

    // 5. USE THE REF (pyodideRef.current)
    const playDrum = (sound: DrumSound, params: KickParams | SnareParams | HatParams, time: number) => {
        // Check the ref
        if (!pyodideRef.current) {
            console.warn("Pyodide not ready, skipping drum trigger.");
            return;
        }
        
        try {
            let pyProxy;
            let p = params as any; 
            let bufferLengthSeconds;
            let finalVolume;
            
            // Get the current pyodide instance from the ref
            const pyodide = pyodideRef.current;

            switch(sound) {
                case 'kick':
                    pyProxy = pyodide.globals.get('generate_kick')(
                        p.pitch,
                        p.decay,
                        p.tone,
                        p.volume
                    );
                    bufferLengthSeconds = p.decay;
                    finalVolume = p.volume;
                    break;
                case 'snare':
                    pyProxy = pyodide.globals.get('generate_snare')(
                        p.decay,
                        p.tone,     // tone_pitch
                        p.noise,    // noise_freq
                        p.volume
                    );
                    bufferLengthSeconds = p.decay * 1.5;
                    finalVolume = p.volume;
                    break;
                case 'closedHat':
                case 'openHat':
                    pyProxy = pyodide.globals.get('generate_hat')(
                        (p as HatParams).pitch,
                        (p as HatParams).decay,
                        (p as HatParams).volume
                    );
                    bufferLengthSeconds = (p as HatParams).decay;
                    finalVolume = (p as HatParams).volume;
                    break;
                default:
                    return;
            }

            // --- Common logic for all drum sounds ---

            // 1. Copy data from Python (64-bit) to JS (32-bit)
            const audioSamples = pyProxy.toJs({ array_buffer_type: "float32" });
            pyProxy.destroy(); // Free memory

            // 2. Create Web Audio Buffer
            // ... (rest of this function is unchanged)
            const buffer = context.createBuffer(
                1, 
                audioSamples.length, 
                context.sampleRate
            );
            buffer.getChannelData(0).set(audioSamples);

            const gainNode = context.createGain();
            gainNode.gain.setValueAtTime(finalVolume, time);
            gainNode.connect(context.destination);

            const source = context.createBufferSource();
            source.buffer = buffer;
            source.connect(gainNode);
            source.start(time);
            source.stop(time + bufferLengthSeconds + 0.05);
            
        } catch (e) {
            console.error(`Pyodide drum error (${sound}):`, e);
        }
    };
    const renderSynthPartToBuffer = (params: SynthParams, sequence: PartSequence, tempo: number): Promise<AudioBuffer> => {
        return new Promise((resolve, reject) => {
            if (rendererWorkerRef.current) {
                rendererWorkerRef.current.terminate();
            }

            const worker = new Worker(new URL('../workers/renderer.worker.ts', import.meta.url), { type: 'module' });
            rendererWorkerRef.current = worker;

            worker.onmessage = (event: MessageEvent<AudioBuffer>) => {
                resolve(event.data);
                worker.terminate();
                rendererWorkerRef.current = null;
            };

            worker.onerror = (error) => {
                console.error("Renderer worker error:", error);
                reject(error);
                worker.terminate();
                rendererWorkerRef.current = null;
            };

            worker.postMessage({
                params,
                sequence,
                tempo,
                sampleRate: context.sampleRate,
                numSteps: NUM_STEPS
            });
        });
    };

    const playBufferedPart = (buffer: AudioBuffer, time: number) => {
        const source = context.createBufferSource();
        source.buffer = buffer;
        source.connect(context.destination);
        source.start(time);
    };

    const playAmbiance = async (url: string) => {
        if (ambianceSourceNodeRef.current) {
            ambianceSourceNodeRef.current.stop();
        }
        if (!url) return;

        let buffer = loadedAmbianceBuffersRef.current.get(url);
        if (!buffer) {
            try {
                const response = await fetch(url);
                if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
                const arrayBuffer = await response.arrayBuffer();
                buffer = await context.decodeAudioData(arrayBuffer);
                loadedAmbianceBuffersRef.current.set(url, buffer);
            } catch (e) {
                console.error("Failed to load ambiance track:", e);
                return;
            }
        }

        if (!ambianceGainNodeRef.current) {
            ambianceGainNodeRef.current = context.createGain();
            ambianceGainNodeRef.current.connect(context.destination);
        }

        const source = context.createBufferSource();
        source.buffer = buffer;
        source.loop = true;
        source.connect(ambianceGainNodeRef.current);
        source.start(0);
        ambianceSourceNodeRef.current = source;
    };

    const stopAmbiance = () => {
        if (ambianceSourceNodeRef.current) {
            ambianceSourceNodeRef.current.stop();
            ambianceSourceNodeRef.current = null;
        }
    };

    const setAmbianceVolume = (volume: number) => {
        if (ambianceGainNodeRef.current) {
            ambianceGainNodeRef.current.gain.setValueAtTime(volume, context.currentTime);
        }
    };

    audioEngineRef.current = { context, playSynth, playDrum, renderSynthPartToBuffer, playBufferedPart, playAmbiance, stopAmbiance, setAmbianceVolume };
    setIsReady(true);
  }, []);

  return { audioEngine: audioEngineRef.current, isReady, initializeAudio };
};
